// Bluetooth FTMS (Fitness Machine Service 0x1826) data decoders.
//
// Each data characteristic begins with a little-endian uint16 flags field;
// optional fields follow in a fixed order, present only when their flag bit is
// set. We walk the buffer with a moving cursor so absent fields are skipped.
//
// References: Bluetooth FTMS spec — Treadmill Data (0x2ACD), Indoor Bike Data
// (0x2AD2), Rower Data (0x2AD1).

import type { MachineKind, TelemetryMetric } from './types'

type ParsedMetrics = Partial<Record<TelemetryMetric, number>>

class Cursor {
  offset = 2 // first 2 bytes are always the flags field
  private readonly view: DataView
  constructor(view: DataView) { this.view = view }
  u8() { const v = this.view.getUint8(this.offset); this.offset += 1; return v }
  u16() { const v = this.view.getUint16(this.offset, true); this.offset += 2; return v }
  s16() { const v = this.view.getInt16(this.offset, true); this.offset += 2; return v }
  u24() {
    const v =
      this.view.getUint8(this.offset) |
      (this.view.getUint8(this.offset + 1) << 8) |
      (this.view.getUint8(this.offset + 2) << 16)
    this.offset += 3
    return v
  }
  get remaining() { return this.view.byteLength - this.offset }
}

const bit = (flags: number, n: number) => (flags & (1 << n)) !== 0

export function parseTreadmillData(view: DataView): ParsedMetrics {
  const flags = view.getUint16(0, true)
  const c = new Cursor(view)
  const m: ParsedMetrics = {}
  try {
    if (!bit(flags, 0)) m.speed_kmh = c.u16() / 100 // instantaneous speed
    if (bit(flags, 1)) c.u16() // average speed
    if (bit(flags, 2)) m.distance_m = c.u24()
    if (bit(flags, 3)) { m.incline_pct = c.s16() / 10; c.s16() /* ramp angle */ }
    if (bit(flags, 4)) { c.u16(); c.u16() /* elevation gain pos/neg */ }
    if (bit(flags, 5)) c.u16() // instantaneous pace
    if (bit(flags, 6)) c.u16() // average pace
    if (bit(flags, 7)) { m.calories_kcal = c.u16(); c.u16(); c.u8() /* energy/hr,/min */ }
    if (bit(flags, 8)) m.heart_rate_bpm = c.u8()
    if (bit(flags, 9)) c.u8() // metabolic equivalent
    if (bit(flags, 10)) m.elapsed_s = c.u16()
  } catch { /* truncated frame */ }
  return m
}

export function parseIndoorBikeData(view: DataView): ParsedMetrics {
  const flags = view.getUint16(0, true)
  const c = new Cursor(view)
  const m: ParsedMetrics = {}
  try {
    if (!bit(flags, 0)) m.speed_kmh = c.u16() / 100
    if (bit(flags, 1)) c.u16() // average speed
    if (bit(flags, 2)) m.cadence_rpm = c.u16() / 2 // 0.5 resolution
    if (bit(flags, 3)) c.u16() // average cadence
    if (bit(flags, 4)) m.distance_m = c.u24()
    if (bit(flags, 5)) m.resistance_level = c.s16()
    if (bit(flags, 6)) m.power_watts = c.s16()
    if (bit(flags, 7)) c.s16() // average power
    if (bit(flags, 8)) { m.calories_kcal = c.u16(); c.u16(); c.u8() }
    if (bit(flags, 9)) m.heart_rate_bpm = c.u8()
    if (bit(flags, 10)) c.u8() // metabolic equivalent
    if (bit(flags, 11)) m.elapsed_s = c.u16()
  } catch { /* truncated frame */ }
  return m
}

export function parseRowerData(view: DataView): ParsedMetrics {
  const flags = view.getUint16(0, true)
  const c = new Cursor(view)
  const m: ParsedMetrics = {}
  try {
    if (!bit(flags, 0)) { m.stroke_rate = c.u8() / 2; m.rep_count = c.u16() }
    if (bit(flags, 1)) c.u8() // average stroke rate
    if (bit(flags, 2)) m.distance_m = c.u24()
    if (bit(flags, 3)) m.split_500m_sec = c.u16() // instantaneous pace, sec/500m
    if (bit(flags, 4)) c.u16() // average pace
    if (bit(flags, 5)) m.power_watts = c.s16()
    if (bit(flags, 6)) c.s16() // average power
    if (bit(flags, 7)) c.s16() // resistance level
    if (bit(flags, 8)) { m.calories_kcal = c.u16(); c.u16(); c.u8() }
    if (bit(flags, 9)) m.heart_rate_bpm = c.u8()
  } catch { /* truncated frame */ }
  return m
}

/** Heart Rate Measurement (0x2A37): flags byte then uint8/uint16 BPM. */
export function parseHeartRateMeasurement(view: DataView): ParsedMetrics {
  try {
    const flags = view.getUint8(0)
    const hr = bit(flags, 0) ? view.getUint16(1, true) : view.getUint8(1)
    return { heart_rate_bpm: hr }
  } catch {
    return {}
  }
}

export function parseFtmsByKind(kind: MachineKind, view: DataView): ParsedMetrics {
  switch (kind) {
    case 'treadmill':
    case 'stair_climber':
    case 'elliptical':
      return parseTreadmillData(view)
    case 'indoor_bike':
      return parseIndoorBikeData(view)
    case 'rower':
    case 'ski_erg':
      return parseRowerData(view)
    default:
      return {}
  }
}
